import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BellRinging, Check, ArrowSquareOut } from "@phosphor-icons/react";

const ALERT_LABELS = {
  below_par: "Below PAR",
  out_of_stock: "Out of Stock",
  count_awaiting_approval: "Count Awaiting Approval",
  sync_failure: "Offline Sync Failed",
  expiring_soon: "Expiring Soon",
};

const STATUS_COLORS = {
  sent: "text-muted-foreground",
  queued: "text-yellow-500",
  acknowledged: "text-blue-500",
  resolved: "text-success",
};

export default function NotificationHistory() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", alert_type: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.status) params.set("status", filter.status);
      if (filter.alert_type) params.set("alert_type", filter.alert_type);
      params.set("limit", "100");
      const { data } = await api.get(`/notifications/history?${params.toString()}`);
      setAlerts(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter.status, filter.alert_type]);

  useEffect(() => { load(); }, [load]);

  const acknowledge = async (id) => {
    try {
      await api.post(`/notifications/history/${id}/acknowledge`, { reason: "user" });
      toast.success("Acknowledged");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-tech mb-1">Alerts</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Notification History</h1>
        </div>
        <Link to="/notifications" className="text-xs text-muted-foreground hover:text-primary">← Preferences</Link>
      </div>

      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <select
          data-testid="history-filter-status"
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          className="h-10 px-2 border border-border bg-background rounded-sm text-sm"
        >
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="queued">Queued</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="resolved">Resolved</option>
        </select>
        <select
          data-testid="history-filter-type"
          value={filter.alert_type}
          onChange={(e) => setFilter({ ...filter, alert_type: e.target.value })}
          className="h-10 px-2 border border-border bg-background rounded-sm text-sm"
        >
          <option value="">All types</option>
          {Object.entries(ALERT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground font-mono">Loading…</div>
      ) : alerts.length === 0 ? (
        <div className="card-flat border-border p-6 text-sm text-muted-foreground text-center">
          <BellRinging size={22} className="mx-auto mb-2 text-muted-foreground" />
          No alerts yet.
        </div>
      ) : (
        <div className="card-flat border-border overflow-hidden" data-testid="history-list">
          {alerts.map((a) => (
            <div key={a.alert_id} className="grid grid-cols-[1fr_auto] gap-2 px-4 py-3 border-b border-border last:border-b-0">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold uppercase tracking-widest bg-primary text-primary-foreground px-1.5 py-0.5">
                    {ALERT_LABELS[a.alert_type] || a.alert_type}
                  </span>
                  <span className={`text-[10px] font-mono uppercase tracking-widest ${STATUS_COLORS[a.status] || ""}`}>
                    {a.status}
                  </span>
                </div>
                <div className="font-bold text-sm truncate">{a.entity_name}</div>
                <div className="text-xs text-muted-foreground font-mono">
                  qty {a.payload?.current_qty ?? "—"} · threshold {a.payload?.threshold ?? "—"} · {a.payload?.location || "—"}
                </div>
                <div className="text-[10px] text-muted-foreground font-mono mt-1">
                  {new Date(a.created_at).toLocaleString()} · recipients {a.recipients?.length || 0}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {a.payload?.deep_link && (
                  <Link
                    to={a.payload.deep_link}
                    className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground hover:text-primary flex items-center gap-1"
                  >
                    Open <ArrowSquareOut size={12} />
                  </Link>
                )}
                {a.status !== "acknowledged" && a.status !== "resolved" && (
                  <button
                    data-testid={`ack-${a.alert_id}`}
                    onClick={() => acknowledge(a.alert_id)}
                    className="btn-hard h-8 px-3 border border-border text-[11px] font-bold uppercase tracking-widest rounded-sm hover:bg-primary hover:text-primary-foreground flex items-center gap-1"
                  >
                    <Check size={12} weight="bold" /> Ack
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
